import { sessionsApi } from "@features/chat/services/api";
import type { projectsApi } from "@features/projects/services/api";
import { toast } from "@stores/toastStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { versionsApi } from "../services/api";
import { versionKeys } from "../services/queryKeys";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Every :SchematicVersion for a project, newest first. */
export function useVersions(projectId: string | null | undefined) {
  return useQuery({
    queryKey: versionKeys.byProject(projectId ?? ""),
    queryFn: () => versionsApi.list(projectId ?? ""),
    enabled: !!projectId,
  });
}

/** A single version with its full change summary. */
export function useVersion(
  projectId: string | null | undefined,
  versionId: string | null | undefined,
) {
  return useQuery({
    queryKey: versionKeys.detail(projectId ?? "", versionId ?? ""),
    queryFn: () => versionsApi.get(projectId ?? "", versionId ?? ""),
    enabled: !!projectId && !!versionId,
  });
}

/** Ref + net-topology diff between any two versions of the same project. */
export function useVersionDiff(
  projectId: string | null | undefined,
  versionAId: string | null | undefined,
  versionBId: string | null | undefined,
) {
  return useQuery({
    queryKey: versionKeys.diff(
      projectId ?? "",
      versionAId ?? "",
      versionBId ?? "",
    ),
    queryFn: () =>
      versionsApi.diff(projectId ?? "", versionAId ?? "", versionBId ?? ""),
    enabled: !!projectId && !!versionAId && !!versionBId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Flip :CURRENT_VERSION to the target version. */
export function useRestoreVersion(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      versionsApi.restore(projectId, versionId),
    onSuccess: (data) => {
      toast.success(`Restored to v${data.version.version_number}`);
      void qc.invalidateQueries({ queryKey: versionKeys.byProject(projectId) });
      void qc.invalidateQueries({ queryKey: ["projects", "list"] });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not restore version"));
    },
  });
}

/** Re-pin an existing chat session to a different version. */
export function useUpdateSessionVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      versionId,
    }: {
      sessionId: string;
      versionId: string;
    }) => sessionsApi.updateVersion(sessionId, versionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sessions"] });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not update session version"));
    },
  });
}

/** Convenience: download the raw source file for a version. */
export function versionDownloadUrl(
  projectId: string,
  versionId: string,
): string {
  return versionsApi.downloadUrl(projectId, versionId);
}

// Helper to silence unused-import checker when callers pull typed responses.
export type { projectsApi };
